/**
 * Sign-up flow — public API.
 *
 * Import from this file, not from the sub-modules directly.
 */

export type {
  SignUpRequiredField,
  SignUpCreateParams,
  SignUpPendingFields,
  SignUpConfig,
  SignUpState,
  SignUpCommand,
  SignUpResult,
  SignUpSync,
  SignUpAction,
  SignUpDeps,
  SignUpStoreDeps,
} from './types';

export { signUpReducer } from './reducer';
export { signUpEffects } from './effects';

import { FlowStore } from '../../shared/flow-store';
import { signUpReducer, INITIAL_STATE } from './reducer';
import { signUpEffects } from './effects';
import { consentGateReducer, effectsWithoutDirectRegistration } from './consent-gate';
import type { SignUpState, SignUpAction, SignUpDeps, SignUpStoreDeps } from './types';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Returns true when the flow is in an async (loading) step. */
export function isSignUpLoading(state: SignUpState): boolean {
  switch (state.step) {
    case 'REGISTERING':
    case 'REGISTERING_AFTER_CONSENT':
    case 'VERIFYING':
    case 'SUBMITTING_PHONE':
      return true;
    default:
      return false;
  }
}

/** Maps a failed command to the appropriate typed failure action. */
export function signUpDeriveFailure(action: SignUpAction, err: unknown): SignUpAction {
  const msg = err instanceof Error ? err.message : 'Something went wrong.';
  switch (action.type) {
    case 'SUBMIT_REGISTRATION': return { type: 'SIGN_UP_FAIL', error: msg };
    case 'SUBMIT_CONSENT':      return { type: 'CONSENT_FAIL', error: msg };
    case 'SUBMIT_VERIFICATION': return { type: 'VERIFICATION_FAIL', error: msg };
    case 'SUBMIT_PHONE':        return { type: 'PHONE_FAIL', error: msg };
    case 'RESEND_CODE':         return { type: 'RESEND_CODE_FAIL', error: msg };
    default:                    return { type: 'RESET' };
  }
}

// ─── Store factory ────────────────────────────────────────────────────────────

/**
 * Creates a fully-wired sign-up store.
 *
 * When `config.consentRequired` is true, SUBMIT_REGISTRATION routes to
 * CONSENT_REQUIRED synchronously (no effect fires) and the user must
 * complete the consent form before the signUp call is made.
 */
export function createSignUpStore(deps: SignUpStoreDeps): FlowStore<SignUpState, SignUpAction, SignUpDeps> {
  const config = deps.config ?? {};

  const reducer = config.consentRequired ? consentGateReducer : signUpReducer;
  const effects = config.consentRequired ? effectsWithoutDirectRegistration : signUpEffects;

  return new FlowStore<SignUpState, SignUpAction, SignUpDeps>({
    name: 'SIGN_UP',
    initialState: INITIAL_STATE,
    reducer,
    effects,
    deriveFailure: signUpDeriveFailure,
    deps: { service: deps.service, observability: deps.observability, eventBus: deps.eventBus, session: deps.session, config },
    observability: deps.observability,
  });
}
